import { useState, useEffect } from "react";
import { View, Text, Button } from "../base";
import { InputField, DropdownField } from "../base";
import { Icon } from "../base/Icon";
import { useAppDispatch } from "../../redux";
import { agentEnvironmentActions } from "../../redux/reducers/agentEnvironmentSlice";

export type ErrorFallbackConfigFormData = {
  id?: string;
  agentId?: string;
  errorFallbackType?: string;
  errorFallbackValue?: string;
  errorFallbackTimeoutSeconds?: number;
  errorFallbackOccurrenceThreshold?: number;
  errorFallbackTimeWindowSeconds?: number;
  errorFallbackWebhookSecret?: string | null;
};

type ErrorFallbackConfigFormProps = {
  config: ErrorFallbackConfigFormData;
  onChange: (key: string) => (value: unknown) => void;
};

export const ErrorFallbackConfigForm = ({ config, onChange }: ErrorFallbackConfigFormProps) => {
  const dispatch = useAppDispatch();
  const [copied, setCopied] = useState(false);
  const [revealedSecret, setRevealedSecret] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [localType, setLocalType] = useState(config.errorFallbackType || "");
  const [localValue, setLocalValue] = useState(config.errorFallbackValue || "");
  const [localTimeout, setLocalTimeout] = useState<number | "">(config.errorFallbackTimeoutSeconds ?? 0);
  const [localThreshold, setLocalThreshold] = useState<number | "">(config.errorFallbackOccurrenceThreshold ?? 5);
  const [localTimeWindow, setLocalTimeWindow] = useState<number | "">(config.errorFallbackTimeWindowSeconds ?? 300);

  // Reset local state when config changes (e.g., navigating to different environment)
  useEffect(() => {
    setLocalType(config.errorFallbackType || "");
    setLocalValue(config.errorFallbackValue || "");
    setLocalTimeout(config.errorFallbackTimeoutSeconds ?? 0);
    setLocalThreshold(config.errorFallbackOccurrenceThreshold ?? 5);
    setLocalTimeWindow(config.errorFallbackTimeWindowSeconds ?? 300);
    setIsSaving(false);
    setRevealedSecret(null);
  }, [config.id]);

  const hasChanges =
    localType !== (config.errorFallbackType || "") ||
    localValue !== (config.errorFallbackValue || "") ||
    localTimeout !== (config.errorFallbackTimeoutSeconds ?? 0) ||
    localThreshold !== (config.errorFallbackOccurrenceThreshold ?? 5) ||
    localTimeWindow !== (config.errorFallbackTimeWindowSeconds ?? 300);

  const onSave = async () => {
    if (!config.id || !config.agentId) return;

    setIsSaving(true);

    try {
      // Update the config fields
      onChange("errorFallbackType")(localType || null);
      onChange("errorFallbackValue")(localValue || null);
      const timeoutValue = localTimeout === "" ? 0 : localTimeout;
      const thresholdValue = localThreshold === "" ? 5 : localThreshold;
      const timeWindowValue = localTimeWindow === "" ? 300 : localTimeWindow;

      onChange("errorFallbackTimeoutSeconds")(timeoutValue);
      onChange("errorFallbackOccurrenceThreshold")(thresholdValue);
      onChange("errorFallbackTimeWindowSeconds")(timeWindowValue);

      await dispatch(
        agentEnvironmentActions.updateById({
          id: config.id,
          agentId: config.agentId,
          errorFallbackType: (localType || null) as "phone" | "webhook" | undefined,
          errorFallbackValue: localValue || undefined,
          errorFallbackTimeoutSeconds: timeoutValue,
          errorFallbackOccurrenceThreshold: thresholdValue,
          errorFallbackTimeWindowSeconds: timeWindowValue,
          deploymentAction: "config",
        }),
      );

      // If setting webhook for the first time, generate and reveal secret
      if (localType === "webhook" && !config.errorFallbackWebhookSecret) {
        const secretResult = await dispatch(
          agentEnvironmentActions.regenerateErrorFallbackWebhookSecret({
            id: config.id,
            agentId: config.agentId,
          }),
        );
        if (agentEnvironmentActions.regenerateErrorFallbackWebhookSecret.fulfilled.match(secretResult)) {
          setRevealedSecret(secretResult.payload.errorFallbackWebhookSecret);
        }
      }
    } catch (error) {
      console.error("Error saving error fallback settings:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const onRegenerateSecret = async () => {
    if (config.id && config.agentId) {
      const result = await dispatch(
        agentEnvironmentActions.regenerateErrorFallbackWebhookSecret({
          id: config.id,
          agentId: config.agentId,
        }),
      );
      if (agentEnvironmentActions.regenerateErrorFallbackWebhookSecret.fulfilled.match(result)) {
        setRevealedSecret(result.payload.errorFallbackWebhookSecret);
      }
    }
  };

  const onCopySecret = async () => {
    if (revealedSecret) {
      await navigator.clipboard.writeText(revealedSecret);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const isWebhook = localType === "webhook";
  const displaySecret = revealedSecret || (config.errorFallbackWebhookSecret ? "•".repeat(40) : null);
  const isSecretRevealed = !!revealedSecret;

  return (
    <View className="space-y-6">
      <View className="space-y-4">
        <DropdownField
          label="Error Fallback Type"
          placeholder="Select fallback type"
          value={localType}
          onChange={(value) => setLocalType(value as string)}
          options={[
            { label: "Disabled", value: "" },
            { label: "Phone", value: "phone" },
            { label: "Webhook", value: "webhook" },
          ]}
        />

        <InputField
          label="Error Fallback Value"
          placeholder={localType === "phone" ? "Enter phone number" : "Enter webhook URL"}
          value={localValue}
          onChange={setLocalValue}
          isDisabled={!localType}
        />

        {isWebhook && displaySecret && (
          <View className="space-y-2">
            <Text className="text-sm font-medium text-gray-700 dark:text-gray-300">Webhook Secret</Text>
            <View className="flex items-center gap-2">
              <View className="flex-1 relative">
                <InputField
                  labelClassName="!w-full"
                  className="!w-full font-mono text-sm"
                  value={displaySecret}
                  readOnly
                />
              </View>
              {isSecretRevealed && (
                <Button
                  variant="neutral"
                  size="sm"
                  outline
                  onClick={onCopySecret}
                >
                  <Icon
                    name={copied ? "check" : "copy"}
                    size={14}
                  />
                </Button>
              )}
              <Button
                variant="neutral"
                size="sm"
                outline
                onClick={onRegenerateSecret}
              >
                <Icon
                  name="refresh"
                  size={14}
                />
              </Button>
            </View>
            <Text className="text-xs text-gray-500 dark:text-gray-400">
              {isSecretRevealed ? "Copy this secret now. For security, it won't be shown again." : "Secret is hidden. Click regenerate to create a new secret."}
            </Text>
          </View>
        )}

        <InputField
          label="Error Fallback Timeout"
          tooltip="The timeout in seconds before the error fallback is triggered. A value of 0 results in no fallback being triggered."
          placeholder="Enter seconds"
          value={localTimeout}
          onChange={(value) => setLocalTimeout(value === "" || value === null || value === undefined ? "" : Number(value))}
          type="number"
          min={0}
          max={30}
          isDisabled={!localType}
        />

        <InputField
          label="Error Fallback Occurrence Threshold"
          tooltip="Number of timeout occurrences across different sessions/calls within the time window before triggering fallback. Default: 5"
          placeholder="Enter threshold"
          value={localThreshold}
          onChange={(value) => setLocalThreshold(value === "" || value === null || value === undefined ? "" : Number(value))}
          type="number"
          min={1}
          max={20}
          isDisabled={!localType}
        />

        <InputField
          label="Error Fallback Time Window"
          tooltip="Time window in seconds to track timeout occurrences across sessions. Default: 300 (5 minutes)"
          placeholder="Enter seconds"
          value={localTimeWindow}
          onChange={(value) => setLocalTimeWindow(value === "" || value === null || value === undefined ? "" : Number(value))}
          type="number"
          min={60}
          max={3600}
          isDisabled={!localType}
        />
      </View>

      <View className="flex justify-end pt-2 border-t border-gray-200 dark:border-gray-700">
        <Button
          variant="success"
          size="sm"
          outline
          onClick={onSave}
          disabled={!hasChanges || isSaving}
        >
          {isSaving ? (
            <Icon
              name="loader"
              size={14}
              className="animate-spin"
            />
          ) : (
            <Icon
              name="check"
              size={14}
            />
          )}
          Save & Deploy
        </Button>
      </View>
    </View>
  );
};
